import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { BulkImportType } from '../../../../../types';
import { Dispatch, SetStateAction } from 'react';
import DriveTransfer from '../../../../assets/drivetransfer.png';
import BulkAddUser from '../../../../assets/bulkadduser.png';
import AbandonedCalendarEvents from '../../../../assets/abandonedCalendarEvents.png';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import SharedFilePickerOld from '../../../../shared/shared-file-picker-old';
import SharedFilePicker from '../../../../shared/shared-file-picker';
import { SectionHeader } from '../../../../shared/section-header';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import PickerTriggerButton from './picker-trigger-button';
import SelectedFileRow from './selected-file-row';
import { useLightestOutline } from './use-lightest-outline';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const helpDocumentationLink = (
  <Link
    href={'https://promevo.com/knowledge/gpanel-support#bulk-operations'}
    id="anchor"
    target="_blank"
    rel="noreferrer noopener"
  >
    help documentation
  </Link>
);

// Design: selected file row and column example share this width.
const contentWidth = 454;

interface StepSpreadsheetProps {
  setValue: UseFormSetValue<BulkOpsFormValues>;
  spreadSheetName: string;
  setSpreadSheetName: Dispatch<SetStateAction<string>>;
  selectedBulkImportType: BulkImportType | null;
  control: Control<BulkOpsFormValues>;
  errors: FieldErrors<BulkOpsFormValues>;
  isNewUi?: boolean;
}

export default function StepSpreadsheet({
  setValue,
  spreadSheetName,
  setSpreadSheetName,
  selectedBulkImportType,
  control,
  errors,
  isNewUi = false,
}: StepSpreadsheetProps) {
  const theme = useTheme();
  const lightestOutline = useLightestOutline();
  const { features } = useUserSettings();
  const FilePicker = isFeatureEnabled('drivePickerUIReact', features)
    ? SharedFilePicker
    : SharedFilePickerOld;
  const columnsExample =
    selectedBulkImportType!.name === 'driveTransfer'
      ? DriveTransfer
      : selectedBulkImportType!.name === 'bulkUploadUsers'
      ? BulkAddUser
      : selectedBulkImportType!.name === 'abandonedCalendarEvents'
      ? AbandonedCalendarEvents
      : null;
  const columnsExampleText =
    'The following image below shows the required headers that must be in your selected spreadsheet for this bulk operation type:';

  return (
    <Stack gap={isNewUi ? 3 : 2} width={'100%'}>
      {isNewUi ? (
        <FormSectionHeader
          descriptionVariant="body2"
          description={
            <>
              Please select a Google Sheet to use for this Bulk Operation. Make
              sure you have the appropriate column headers, see our{' '}
              {helpDocumentationLink} for assistance.
            </>
          }
        >
          Choose a Spreadsheet
        </FormSectionHeader>
      ) : (
        <SectionHeader
          description={
            <>
              Please select a Google Sheet to use for this Bulk Operation. Make
              sure you have the appropriate column headers, see our{' '}
              {helpDocumentationLink} for assistance.
            </>
          }
        >
          Choose Spreadsheet
        </SectionHeader>
      )}
      <Controller
        name="spreadsheetId"
        control={control}
        render={({ field }) => {
          const handleFileRemoved = () => {
            field.onChange('');
            setSpreadSheetName('');
          };
          return (
            <Stack gap={2} sx={isNewUi ? { width: contentWidth } : undefined}>
              <FilePicker
                pickerType="spreadsheet"
                selectedFileId={field.value || ''}
                selectedFileName={spreadSheetName}
                onFileSelected={({ id, name }) => {
                  field.onChange(id);
                  setValue('spreadsheetId', id);
                  setSpreadSheetName(name);
                }}
                onFileRemoved={handleFileRemoved}
                errorMessage={errors.spreadsheetId?.message}
                includeSharedDrives
                customTrigger={
                  isNewUi
                    ? (openPicker) => (
                        <PickerTriggerButton
                          noun="File"
                          hasSelection={Boolean(field.value)}
                          onClick={openPicker}
                        />
                      )
                    : undefined
                }
              />
              {isNewUi && field.value && (
                <SelectedFileRow
                  name={spreadSheetName}
                  onRemove={handleFileRemoved}
                />
              )}
            </Stack>
          );
        }}
      />
      {columnsExample && isNewUi && (
        <Stack
          gap={2}
          alignItems="center"
          sx={{
            width: contentWidth,
            p: '17px',
            borderRadius: 2,
            border: '1px solid',
            borderColor: lightestOutline,
            backgroundColor: theme.palette.semantic.surface.bg1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.palette.semantic.text.support }}
          >
            {columnsExampleText}
          </Typography>
          <img src={columnsExample} alt="Columns Example" width={420} />
        </Stack>
      )}
      {columnsExample && !isNewUi && (
        <Stack gap={3} sx={{ width: '100%' }}>
          <Typography>{columnsExampleText}</Typography>
          <img src={columnsExample} alt="Columns Example" width={400} />
        </Stack>
      )}
    </Stack>
  );
}
